// Lineage drawer — slides in from the right showing the full parent
// chain of an event. Origin (depth 0) at the top, requested event at
// the bottom. "Reprocess input ↻" re-fires current triggers on the
// chain's root input event (idempotent — same event_key, no duplicates).
//
// FileUploadMetaCard co-located here — only consumer is the lineage
// rendering of file_upload_meta / file_storage origin events. Pulling it
// into its own file produces a single-consumer cross-import for no gain.
//
// Extracted from pages/SourceDetailPage.tsx as part of the file split
// (Apr 2026).

import React, { useEffect, useState } from 'react';
import type { DataHubEvent, Trigger } from '@datahub/shared';
import { getEventLineage, reprocessEvent } from '../../api';
import { JsonTreeWithControls } from '../../lib/json-tree';

export function LineageDrawer({ sourceId, eventId, triggers, onClose }: {
  sourceId: string;
  eventId: string;
  triggers: Trigger[];
  onClose: () => void;
}) {
  const [chain, setChain] = useState<DataHubEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reprocessing, setReprocessing] = useState(false);
  const [reprocessMsg, setReprocessMsg] = useState<string | null>(null);

  useEffect(() => {
    setChain(null); setError(null); setReprocessMsg(null);
    getEventLineage(sourceId, eventId)
      .then(r => setChain(r.chain))
      .catch(e => setError(String(e)));
  }, [sourceId, eventId]);

  // Close drawer on Escape — operator's natural reflex.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  function triggerNameFor(triggerId: string): string {
    return triggers.find(t => t.trigger_id === triggerId)?.name ?? triggerId;
  }

  async function handleReprocess() {
    if (!chain || chain.length === 0) return;
    const input = chain[0];
    if (!input) return;
    setReprocessing(true); setReprocessMsg(null);
    try {
      await reprocessEvent(sourceId, input.event_id);
      setReprocessMsg(`Re-fired triggers on ${input.event_id}. Derivatives are idempotent — count won't double.`);
      // Reload chain after a brief delay so any new derivatives propagate to preview store.
      setTimeout(() => {
        getEventLineage(sourceId, eventId).then(r => setChain(r.chain)).catch(() => undefined);
      }, 1500);
    } catch (e) {
      setReprocessMsg(`Failed: ${(e as Error).message}`);
    } finally {
      setReprocessing(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', justifyContent: 'flex-end', zIndex: 1000,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 'min(560px, 100vw)', height: '100vh',
          background: 'var(--bg)', borderLeft: '1px solid var(--line)',
          padding: 24, overflowY: 'auto', boxShadow: '-8px 0 32px rgba(0,0,0,0.2)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.14em', color: 'var(--ink-3)', marginBottom: 4 }}>
              event lineage
            </div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 13, color: 'var(--ink)' }}>{eventId}</div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {chain && chain.length > 0 && (
              <button
                className="btn sm"
                disabled={reprocessing}
                onClick={handleReprocess}
                title="Re-fire current triggers on the input event (idempotent — same event_key, no duplicates)"
              >
                {reprocessing ? 'reprocessing…' : 'reprocess input ↻'}
              </button>
            )}
            <button className="btn sm ghost" onClick={onClose}>Close</button>
          </div>
        </div>
        {reprocessMsg && (
          <div style={{ marginBottom: 14, padding: 10, background: reprocessMsg.startsWith('Failed') ? 'oklch(0.95 0.04 25)' : 'var(--accent-soft)', border: '1px solid var(--line)', borderRadius: 6, fontSize: 12, color: 'var(--ink-2)' }}>
            {reprocessMsg}
          </div>
        )}

        {!chain && !error && <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>Loading lineage…</div>}
        {error && (
          <div style={{ padding: 18, background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 8, fontSize: 13, color: 'var(--ink-2)' }}>
            {error.includes('event_not_found') || error.includes('404')
              ? 'Event not in 24-hour preview window. Older events live in durable storage — open them via Replay.'
              : error}
          </div>
        )}

        {chain && chain.length > 0 && (
          <div>
            <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 14 }}>
              Chain depth: <strong>{chain.length - 1}</strong> · {chain.length} event{chain.length === 1 ? '' : 's'}
            </div>
            <div style={{ position: 'relative' }}>
              {/* Vertical connector line */}
              <div style={{ position: 'absolute', left: 11, top: 12, bottom: 12, width: 2, background: 'var(--line)' }} />

              {chain.map((ev, i) => {
                const isInput = ev.origin.kind === 'input' && ev.origin.id === 'webhook';
                const isReingest = ev.origin.kind === 'input' && ev.origin.id === 'reingest';
                const isDerivative = ev.origin.kind === 'trigger';
                const isFileUpload = ev.origin.kind === 'input' && ev.origin.id === 'file_upload';
                const isApiPull = ev.origin.kind === 'input' && ev.origin.id === 'api_pull';
                const dotColor =
                  isDerivative ? 'oklch(0.78 0.12 250)' :
                  isReingest ? 'oklch(0.78 0.12 75)' :
                  isFileUpload ? 'oklch(0.78 0.12 145)' :
                  isApiPull ? 'oklch(0.78 0.12 200)' :
                  'var(--accent)';
                const stepLabel =
                  isInput ? 'webhook input' :
                  isReingest ? `reingest via ${triggerNameFor(ev.origin.parent_trigger?.id ?? '')}` :
                  isDerivative ? `derive via ${triggerNameFor(ev.origin.id)} v${ev.origin.trigger_version ?? 1}` :
                  isFileUpload ? `file_upload · ${ev.origin.source_filename ?? '?'} (row ${(ev.origin.source_row_index ?? 0) + 1})` :
                  isApiPull ? 'api_pull input' :
                  ev.origin.id;
                return (
                  <div key={ev.event_id} style={{ position: 'relative', paddingLeft: 32, paddingBottom: 18 }}>
                    <div style={{
                      position: 'absolute', left: 4, top: 4,
                      width: 16, height: 16, borderRadius: '50%',
                      background: dotColor,
                      border: '3px solid var(--bg)',
                    }} />
                    <div style={{ background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 8, padding: 14 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, padding: '2px 6px', background: 'var(--bg-sunken)', borderRadius: 3, color: 'var(--ink-3)' }}>
                          d{ev.derivation_depth}
                        </span>
                        <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--ink)' }}>{stepLabel}</span>
                      </div>
                      <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)', marginBottom: 4 }}>
                        {ev.event_id} · {new Date(ev.received_at).toLocaleTimeString()}
                      </div>
                      {isFileUpload && ev.origin.upload_id && (
                        <div style={{
                          marginTop: 6, marginBottom: 8, padding: '6px 8px',
                          background: 'var(--bg-sunken)', borderRadius: 4,
                          fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--ink-2)', lineHeight: 1.6,
                        }}>
                          <div>upload: <span style={{ color: 'var(--ink)' }}>{ev.origin.upload_id}</span> · format: <span style={{ color: 'var(--ink)' }}>{ev.origin.archive_format}</span></div>
                          <div>file: <span style={{ color: 'var(--ink)' }}>{ev.origin.source_filename}</span> · row <span style={{ color: 'var(--ink)' }}>{(ev.origin.source_row_index ?? 0) + 1}</span> · parser: <span style={{ color: 'var(--ink)' }}>{ev.origin.parse_format}</span></div>
                        </div>
                      )}
                      {ev.origin.id === 'file_upload_meta' || ev.origin.id === 'file_storage'
                        ? <FileUploadMetaCard payload={ev.payload as Record<string, unknown>} kind={ev.origin.id} />
                        : (
                          // Replaced the 600-char-truncated <pre> dump
                          // with JsonTree (2026-05-12): full payload
                          // reachable, large nodes collapse based on
                          // serialised size so a 30KB payload doesn't
                          // dominate the drawer. Operator can expand
                          // any node, copy raw JSON, or expand-all /
                          // collapse-all from the toolbar.
                          <JsonTreeWithControls value={ev.payload} compact />
                        )}
                    </div>
                    {i < chain.length - 1 && (
                      <div style={{ marginTop: 6, marginLeft: 4, fontSize: 11, color: 'var(--ink-3)', fontStyle: 'italic' }}>
                        ↓ becomes
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Pretty card for file_upload_meta + file_storage origin events. The
 *  raw payload dump in the lineage drawer was unreadable for upload
 *  events — operator wants to know "did the file land, how big, how
 *  many rows, can I download it" at a glance. Falls back to nothing if
 *  the payload doesn't have the expected shape (tolerant to schema
 *  drift). Kind 'file_storage' = attachment-only mode (no parsing);
 *  kind 'file_upload_meta' = parse-and-ingest summary (rows count +
 *  per-file breakdown). */
function FileUploadMetaCard({ payload, kind }: { payload: Record<string, unknown>; kind: 'file_upload_meta' | 'file_storage' }): React.ReactElement {
  const filename = String(payload.filename ?? payload.original_filename ?? 'upload');
  const contentType = String(payload.content_type ?? '');
  const sizeBytes = Number(payload.size_bytes ?? 0);
  const archiveFormat = String(payload.archive_format ?? '');
  const totalRows = typeof payload.total_rows === 'number' ? payload.total_rows : null;
  const downloadUrl = typeof payload.download_url === 'string' ? payload.download_url : null;
  const signedUrl24h = typeof payload.signed_url_24h === 'string' ? payload.signed_url_24h : null;
  const gcsPath = typeof payload.gcs_path === 'string' ? payload.gcs_path : null;
  const filesProcessed = Array.isArray(payload.files_processed) ? payload.files_processed as Array<Record<string, unknown>> : [];
  const fileErrors = Array.isArray(payload.file_errors) ? payload.file_errors as Array<Record<string, unknown>> : [];

  const fmtSize = (b: number): string => b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1024 / 1024).toFixed(2)} MB`;
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  const fileGlyph = ['xlsx', 'xls'].includes(ext) ? '📊' : ['zip', 'gz', 'tar'].includes(ext) ? '🗜️' : ['csv', 'tsv'].includes(ext) ? '📋' : ['json', 'ndjson', 'jsonl'].includes(ext) ? '{ }' : '📄';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 12 }}>
      {/* Header row: glyph + filename + meta */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 18, lineHeight: 1 }}>{fileGlyph}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 500, color: 'var(--ink)', wordBreak: 'break-all' }}>{filename}</div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--ink-3)', marginTop: 2 }}>
            {[
              ext.toUpperCase() || (contentType.split('/')[1] ?? contentType),
              sizeBytes > 0 ? fmtSize(sizeBytes) : null,
              archiveFormat && archiveFormat !== 'plain' ? `archive: ${archiveFormat}` : null,
              kind === 'file_storage' ? 'stored as attachment' : null,
            ].filter(Boolean).join(' · ')}
          </div>
        </div>
        {totalRows !== null && (
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 16, fontWeight: 600, color: 'oklch(0.42 0.12 150)' }}>{totalRows.toLocaleString()}</div>
            <div style={{ fontSize: 10, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'var(--mono)' }}>rows</div>
          </div>
        )}
      </div>

      {/* Per-file breakdown — useful for ZIP/tar.gz with multiple inner files */}
      {filesProcessed.length > 0 && (
        <div style={{ border: '1px solid var(--line)', borderRadius: 4, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--mono)', fontSize: 11 }}>
            <thead style={{ background: 'var(--bg-sunken)' }}>
              <tr>
                <th style={{ padding: '4px 8px', textAlign: 'left', color: 'var(--ink-3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 10 }}>file</th>
                <th style={{ padding: '4px 8px', textAlign: 'left', color: 'var(--ink-3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 10 }}>format</th>
                <th style={{ padding: '4px 8px', textAlign: 'right', color: 'var(--ink-3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 10 }}>rows</th>
              </tr>
            </thead>
            <tbody>
              {filesProcessed.map((f, i) => (
                <tr key={i} style={{ borderTop: i > 0 ? '1px solid var(--line)' : 'none' }}>
                  <td style={{ padding: '4px 8px', color: 'var(--ink-2)', wordBreak: 'break-all' }}>{String(f.filename ?? '?')}</td>
                  <td style={{ padding: '4px 8px', color: 'var(--ink-3)' }}>{String(f.format ?? '?')}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right', color: 'var(--ink-2)' }}>{Number(f.row_count ?? 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Per-file failures (corrupt entries inside an archive) */}
      {fileErrors.length > 0 && (
        <div style={{ padding: '6px 8px', background: 'oklch(0.95 0.04 30)', border: '1px solid oklch(0.78 0.10 30)', borderRadius: 4, fontSize: 11, color: 'oklch(0.32 0.07 28)' }}>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>{fileErrors.length} file{fileErrors.length === 1 ? '' : 's'} failed</div>
          {fileErrors.slice(0, 3).map((e, i) => (
            <div key={i} style={{ fontFamily: 'var(--mono)', fontSize: 10.5 }}>· {String(e.filename ?? '?')}: {String(e.error ?? 'unknown error')}</div>
          ))}
          {fileErrors.length > 3 && <div style={{ fontFamily: 'var(--mono)', fontSize: 10.5 }}>· +{fileErrors.length - 3} more</div>}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {downloadUrl && (
          <a
            href={downloadUrl}
            download={filename}
            className="btn sm ghost"
            style={{ textDecoration: 'none', fontSize: 11 }}
            title="Download original bytes"
          >↓ download</a>
        )}
        {signedUrl24h && (
          <button
            className="btn sm ghost"
            onClick={() => {
              void navigator.clipboard.writeText(signedUrl24h);
            }}
            style={{ fontSize: 11 }}
            title="Copy 24h signed URL"
          >🔗 signed URL</button>
        )}
        {gcsPath && (
          <button
            className="btn sm ghost"
            onClick={() => void navigator.clipboard.writeText(gcsPath)}
            style={{ fontSize: 11, fontFamily: 'var(--mono)' }}
            title={`Copy ${gcsPath}`}
          >📋 gcs_path</button>
        )}
      </div>
    </div>
  );
}
